import { Controller } from "@hotwired/stimulus"

// TomSelect emits one change per item, including while it restores the
// server-rendered selection on connect, so company changes are coalesced
// before the options frame is reloaded.
const COMPANY_CHANGE_DEBOUNCE_MS = 250

export default class extends Controller {
  static values = {
    assetExpirationTypesUrl: String,
    companyIds: Array
  }

  static targets = [
    "alertTypeInputs",
    "webhookInputs",
    "webhookVariables",
    "webhookOptionalLabel",
    "expirationInputs",
    "recordChangeInputs",
    "recordTypeSelect",
    "assetLayoutGroup",
    "expirationTypeSelect",
    "assetExpirationTypeGroup",
    "assetExpirationTypeFrame",
    "expirationWebhookVars",
    "singleExpirationWebhookVars",
    "websiteMonitorWebhookVars",
    "passwordViewWebhookVars",
    "recordChangeWebhookVars",
    "passwordChangeWebhookVars",
    "emailGroup",
    "webhookRequiredField",
    "webhookRequiredLabel"
  ]

  connect() {
    this.updateVisibility()
  }

  disconnect() {
    clearTimeout(this.companyChangeTimeout)
  }

  updateAlertTypeInputs() {
    this.updateVisibility()
  }

  companiesChanged(event) {
    // TomSelect's search box sits alongside the real select and emits its own
    // change events on blur.
    if (event.target.tagName !== 'SELECT') return

    const ids = Array.from(event.target.options)
      .filter(option => option.selected)
      .map(option => option.value)
      .filter(value => value !== '')

    clearTimeout(this.companyChangeTimeout)
    this.companyChangeTimeout = setTimeout(() => {
      this.reloadAssetExpirationTypes(ids)
    }, COMPANY_CHANGE_DEBOUNCE_MS)
  }

  // Reloading discards the current selection, so skip it when the companies
  // ended up unchanged (a reorder, or TomSelect replaying its initial items).
  reloadAssetExpirationTypes(companyIds) {
    if (!this.hasAssetExpirationTypeFrameTarget || !this.hasAssetExpirationTypesUrlValue) return
    if (this.sameCompanies(companyIds, this.pendingCompanyIds || this.companyIdsValue)) return

    this.pendingCompanyIds = companyIds

    const url = new URL(this.assetExpirationTypesUrlValue, window.location.origin)
    companyIds.forEach(id => url.searchParams.append('company_ids[]', id))
    this.assetExpirationTypeFrameTarget.src = url.toString()
  }

  // companyIdsValue tracks what the frame is actually showing, so it is only
  // committed once the new options render. Turbo cancels a superseded request
  // without erroring, so the last navigation to finish is the current one.
  assetExpirationTypesLoaded() {
    if (this.pendingCompanyIds) {
      this.companyIdsValue = this.pendingCompanyIds
      this.pendingCompanyIds = null
    }

    this.updateAssetExpirationTypeVisibility()
  }

  // Without this the tracked companies would stay ahead of the rendered ones
  // and reselecting them would be treated as a no-op.
  assetExpirationTypesFailed() {
    this.pendingCompanyIds = null
  }

  sameCompanies(a, b) {
    if (a.length !== b.length) return false

    const known = new Set(b.map(String))
    return a.every(id => known.has(String(id)))
  }

  updateRecordTypeInputs() {
    this.updateAssetLayoutVisibility()
  }

  updateExpirationTypeInputs() {
    this.updateAssetExpirationTypeVisibility()
  }

  updateAssetExpirationTypeVisibility() {
    if (!this.hasAssetExpirationTypeGroupTarget || !this.hasExpirationTypeSelectTarget) return

    const alertType = this.getSelectedAlertType()
    const shouldShow =
      (alertType === 'expiration' || alertType === 'single_expiration') &&
      this.expirationTypeSelectTarget.value === 'asset_field'

    this.assetExpirationTypeGroupTarget.classList.toggle('hidden', !shouldShow)
  }

  updateAssetLayoutVisibility() {
    if (!this.hasAssetLayoutGroupTarget || !this.hasRecordTypeSelectTarget) return

    const shouldShow =
      this.getSelectedAlertType() === 'record_change' &&
      this.recordTypeSelectTarget.value === 'Asset'

    this.assetLayoutGroupTarget.classList.toggle('hidden', !shouldShow)
  }

  updateVisibility() {
    const selectedAlertType = this.getSelectedAlertType()
    this.hideAllSections()
    this.hideAllWebhookVariables()

    switch (selectedAlertType) {
      case 'single_expiration':
        this.showTarget('expirationInputs')
        this.showTarget('singleExpirationWebhookVars')
        break
      case 'expiration':
        this.showTarget('expirationInputs')
        this.showTarget('expirationWebhookVars')
        break
      case 'record_change':
        this.showTarget('recordChangeInputs')
        this.showTarget('recordChangeWebhookVars')
        break
      case 'website_monitor':
      case 'password_view':
      case 'password_change':
        if (selectedAlertType === 'website_monitor') {
          this.showTarget('websiteMonitorWebhookVars')
        } else if (selectedAlertType === 'password_view') {
          this.showTarget('passwordViewWebhookVars')
        } else if (selectedAlertType === 'password_change') {
          this.showTarget('passwordChangeWebhookVars')
        }
        break
    }

    if (selectedAlertType === 'record_change') {
      this.emailGroupTarget.classList.add('hidden')
      this.webhookOptionalLabelTarget.classList.add('hidden')
    } else {
      this.emailGroupTarget.classList.remove('hidden')
      this.webhookOptionalLabelTarget.classList.remove('hidden')
    }

    this.updateWebhookRequirement(selectedAlertType === 'record_change')
    this.updateAssetLayoutVisibility()
    this.updateAssetExpirationTypeVisibility()
  }

  // Record change alerts deliver over the webhook instead of email, so the
  // model requires both webhook fields for that type and only that type.
  updateWebhookRequirement(isRequired) {
    this.webhookRequiredFieldTargets.forEach(field => {
      field.dataset.requiredInputRequiredValue = isRequired
    })

    this.webhookRequiredLabelTargets.forEach(label => {
      label.classList.toggle('required', isRequired)
    })
  }

  getSelectedAlertType() {
    const checkedRadio = this.element.querySelector('input[name="alert[alert_type]"]:checked')
    return checkedRadio ? checkedRadio.value : null
  }

  hideAllSections() {
    const sections = this.element.querySelectorAll('.alert-type-section')
    sections.forEach(section => {
      section.classList.add('hidden')
    })
  }

  hideAllWebhookVariables() {
    const webhookSections = this.element.querySelectorAll('.webhook-vars-section')
    webhookSections.forEach(section => {
      section.classList.add('hidden')
    })
  }

  showTarget(targetName) {
    const capitalized = targetName.charAt(0).toUpperCase() + targetName.slice(1)
    if (this[`has${capitalized}Target`]) {
      this[`${targetName}Target`].classList.remove('hidden')
    }
  }

} 